import { createContext, useContext, useEffect, useState } from "react";
import { api, refreshSession, setSession, clearSession, logoutRequest, setSessionEndHandler } from "./api";
import { useAppCache } from "./appcache";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

// Flag NON sensibile: indica solo che potrebbe esistere un cookie di sessione.
const SESSION_FLAG = "maven_session";

export function AuthProvider({ children }) {
  const cache = useAppCache();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(() => !!localStorage.getItem(SESSION_FLAG));
  // Verifica sessione non completata per errore temporaneo (nessun logout).
  const [authUnavailable, setAuthUnavailable] = useState(false);

  const dropLocal = () => {
    clearSession();
    localStorage.removeItem(SESSION_FLAG);
    cache.clearAll(); // nessun dato privato sopravvive alla sessione
    setUser(null);
  };

  const checkSession = () => {
    if (!localStorage.getItem(SESSION_FLAG)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setAuthUnavailable(false);
    refreshSession()
      .then((d) => setUser(d.user))
      .catch((e) => {
        const s = e.response?.status;
        if (s === 401 || s === 403) dropLocal();
        else setAuthUnavailable(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    // Credenziali delle versioni precedenti (token in localStorage): rimosse.
    localStorage.removeItem("maven_token");
    localStorage.removeItem("maven_user");
    setSessionEndHandler(() => {
      dropLocal();
      if (window.location.pathname !== "/login") window.location.href = "/login";
    });
    // Ogni rinnovo riporta ruolo e dati del profilo aggiornati.
    const onRenew = (e) => setUser(e.detail);
    window.addEventListener("maven:session", onRenew);
    checkSession();
    return () => window.removeEventListener("maven:session", onRenew);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    cache.clearAll(); // clean slate on new session
    setSession(r.data);
    localStorage.setItem(SESSION_FLAG, "1");
    setAuthUnavailable(false);
    setUser(r.data.user);
    return r.data.user;
  };
  const logout = async () => {
    await logoutRequest();
    dropLocal();
    setAuthUnavailable(false);
  };

  return (
    <AuthCtx.Provider value={{ user, login, logout, loading, authUnavailable, retrySession: checkSession }}>
      {children}
    </AuthCtx.Provider>
  );
}
